import { useEffect, useMemo, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { authClient } from '../../utils/amplifyClient';
import { PMPartsAndProcedure } from '../../types/pmTypes';
import { VendorList } from '../../types/schema';
import {
    listServiceItemsByCompanyId,
    createServiceItem,
    listServiceProceduresByCompanyId,
    createServiceProcedure,
} from '../../graphql/companyResourceQueries';
import { listVendorListByCompanyIdAndUpdatedAt } from '../../graphql/queries';
import { createVendorList as createVendorListMutation } from '../../graphql/mutations';
import {
    filterServiceItems,
    filterServiceProcedures,
    sortServiceItemsForListing,
    findExistingCategoryName,
    findMatrixProcedure,
    isProcedureType,
    nextCategorySortOrder,
} from '../../utils/companyResourceUtils';
import { findPartVendorConflict } from '../pages-forms/formBuilderServiceCatalog';
import { useUserContext } from '../../contexts/UserContext';
import { createExpenseCategory, fetchExpenseCategoriesByCompany } from '../../graphql/costTrackingQueries';
import type { ExpenseCategory } from '../../types/costTrackingTypes';
import type { ServiceProcedure } from '../../types/companyResourceTypes';
import { Btn, InputField, SearchSuggest, TabNav, getTabPanelProps } from '../ui';
import '../../css/PMEquipmentConfiguration.css';

type TabId = 'consumable' | 'procedure';

interface AddToMatrixModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddItem: (item: PMPartsAndProcedure) => void;
    companyId: string;
    excludeIds: string[];
    defaultTab?: 'consumable' | 'procedure';
}

export const AddToMatrixModal = ({
    isOpen,
    onClose,
    onAddItem,
    companyId,
    excludeIds,
    defaultTab = 'consumable',
}: AddToMatrixModalProps) => {
    const { company, companyUser, companyOwner } = useUserContext();
    const currentUserId = companyUser?.id || companyOwner?.id || '';
    const currentUserName = companyUser?.name || companyOwner?.name || '';
    const costTrackingEnabled = Boolean(company?.costTrackingModuleEnrollment);

    const [activeTab, setActiveTab] = useState<TabId>(defaultTab);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [catalogItems, setCatalogItems] = useState<PMPartsAndProcedure[]>([]);
    const [procedures, setProcedures] = useState<ServiceProcedure[]>([]);
    const [vendors, setVendors] = useState<VendorList[]>([]);
    const [showInlineForm, setShowInlineForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [newItemName, setNewItemName] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [newManufacturer, setNewManufacturer] = useState('');
    const [newPartNumber, setNewPartNumber] = useState('');
    const [newVendor, setNewVendor] = useState('');
    const [newVendorId, setNewVendorId] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newUnit, setNewUnit] = useState('Each');
    const [showNewVendorInput, setShowNewVendorInput] = useState(false);
    const [newVendorName, setNewVendorName] = useState('');

    const [newVendorAddress, setNewVendorAddress] = useState('');
    const [newVendorPhone, setNewVendorPhone] = useState('');
    const [newVendorFax, setNewVendorFax] = useState('');
    const [newVendorRepName, setNewVendorRepName] = useState('');
    const [newVendorRepPhone, setNewVendorRepPhone] = useState('');

    const [newProcedureName, setNewProcedureName] = useState('');
    const [newProcedureCategory, setNewProcedureCategory] = useState('');
    const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
    const [expenseCategoryId, setExpenseCategoryId] = useState('');

    useEffect(() => {
        if (!isOpen || !companyId) return;

        let isCancelled = false;
        setActiveTab(defaultTab);
        setSearch('');
        setSelectedCategory('');
        setShowInlineForm(false);
        setSaveError('');

        (async () => {
            try {
                const [items, procedureRows, vendorResult] = await Promise.all([
                    listServiceItemsByCompanyId(companyId),
                    listServiceProceduresByCompanyId(companyId),
                    authClient.graphql({
                        query: listVendorListByCompanyIdAndUpdatedAt,
                        variables: { companyId, limit: 500 },
                    }),
                ]);
                if (isCancelled) return;
                setCatalogItems(items);
                setProcedures(procedureRows);
                setVendors(vendorResult.data.listVendorListByCompanyIdAndUpdatedAt.items as VendorList[]);
            } catch (err) {
                if (!isCancelled) console.error('Failed to fetch catalog data:', err);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, companyId, defaultTab]);

    useEffect(() => {
        if (!isOpen || !companyId || !costTrackingEnabled) return;
        let isCancelled = false;
        void fetchExpenseCategoriesByCompany(companyId)
            .then((rows) => {
                if (isCancelled) return;
                setExpenseCategories(
                    (rows || [])
                        .filter((row) => Boolean(row?.id && row.name))
                        .map((row) => ({
                            id: row.id,
                            companyId: row.companyId,
                            name: row.name,
                            startingBalance: row.startingBalance ?? 0,
                            createdAt: row.createdAt,
                            updatedAt: row.updatedAt,
                        })),
                );
            })
            .catch((err) => {
                if (!isCancelled) console.error('Failed to load cost categories:', err);
            });
        return () => {
            isCancelled = true;
        };
    }, [isOpen, companyId, costTrackingEnabled]);

    const consumableCategories = useMemo(() => {
        const names = catalogItems
            .filter((item) => item.category && !isProcedureType(item.itemType))
            .map((item) => item.category as string);
        return [...new Set(names)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [catalogItems]);

    const procedureCategories = useMemo(() => {
        const names = procedures.map((procedure) => procedure.category?.trim()).filter((name): name is string => Boolean(name));
        return [...new Set(names)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [procedures]);

    const categoryOptions = (activeTab === 'consumable' ? consumableCategories : procedureCategories).map((name) => ({
        value: name,
        label: name,
    }));

    const filteredConsumables = useMemo(() => {
        if (!selectedCategory) return [];
        return sortServiceItemsForListing(
            filterServiceItems(
                catalogItems.filter((item) => !excludeIds.includes(item.id) && !isProcedureType(item.itemType)),
                search,
                selectedCategory,
            ),
            vendors,
        );
    }, [catalogItems, excludeIds, search, selectedCategory, vendors]);

    const filteredProcedures = useMemo(() => {
        if (!selectedCategory) return [];
        return filterServiceProcedures(procedures, search, selectedCategory).filter((procedure) => {
            const matrixItem = findMatrixProcedure(catalogItems, procedure.itemName);
            return !matrixItem || !excludeIds.includes(matrixItem.id);
        });
    }, [procedures, catalogItems, excludeIds, search, selectedCategory]);

    const resetInlineForm = () => {
        setNewItemName('');
        setNewCategory('');
        setNewManufacturer('');
        setNewPartNumber('');
        setNewVendor('');
        setNewVendorId('');
        setNewPrice('');
        setNewUnit('Each');
        setNewProcedureName('');
        setNewProcedureCategory('');
        setExpenseCategoryId('');
        setShowNewVendorInput(false);
        setNewVendorName('');
        setNewVendorAddress('');
        setNewVendorPhone('');
        setNewVendorFax('');
        setNewVendorRepName('');
        setNewVendorRepPhone('');
    };

    const handleClose = () => {
        setSearch('');
        setSelectedCategory('');
        setShowInlineForm(false);
        setSaveError('');
        resetInlineForm();
        onClose();
    };

    const resolveCategory = (typed: string, known: string[]) =>
        findExistingCategoryName(known, typed) || typed.trim();

    const manufacturersForCategory = useMemo(() => {
        const category = resolveCategory(newCategory, consumableCategories);
        if (!category) return [];
        const names = new Set<string>();
        catalogItems
            .filter((item) => !isProcedureType(item.itemType) && item.manufacturer)
            .forEach((item) => {
                if (findExistingCategoryName([item.category || ''], category) && item.manufacturer) {
                    names.add(item.manufacturer);
                }
            });
        return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [catalogItems, consumableCategories, newCategory]);

    const ensureProcedureOnMatrix = async (itemName: string, category: string) => {
        const existing = findMatrixProcedure(catalogItems, itemName);
        if (existing) return existing;
        const item = await createServiceItem({
            companyId,
            itemName: itemName.trim(),
            category,
            itemType: 'Procedure',
            createdByUserId: currentUserId || undefined,
            createdByUserName: currentUserName || undefined,
        });
        if (!item) throw new Error('Failed to add procedure to the matrix');
        return item as PMPartsAndProcedure;
    };

    const handleSelectItem = (item: PMPartsAndProcedure) => {
        onAddItem(item);
        handleClose();
    };

    const handleSaveNewConsumable = async () => {
        const category = resolveCategory(newCategory, consumableCategories);
        if (!newItemName.trim() || !category) return;
        setSaving(true);
        setSaveError('');
        try {
            let vendorId = newVendorId;
            let vendorName = newVendor;
            if (showNewVendorInput && newVendorName.trim()) {
                const vendorResult = await authClient.graphql({
                    query: createVendorListMutation,
                    variables: {
                        input: {
                            companyId,
                            name: newVendorName.trim(),
                            address: newVendorAddress.trim() || null,
                            phoneNumber: newVendorPhone.trim() || null,
                            faxNumber: newVendorFax.trim() || null,
                            repName: newVendorRepName.trim() || null,
                            repPhoneNumber: newVendorRepPhone.trim() || null,
                        },
                    },
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const created = (vendorResult as any).data.createVendorList;
                vendorId = created.id;
                vendorName = created.name;
            }

            const manufacturer =
                manufacturersForCategory.find(
                    (name) => name.toLowerCase() === newManufacturer.trim().toLowerCase(),
                ) || newManufacturer.trim();

            const partConflict = findPartVendorConflict(catalogItems, {
                partNumber: newPartNumber,
                manufacturer,
                vendorId: vendorId || null,
                vendorName: vendorName || null,
            });
            if (partConflict) {
                setSaveError(
                    'This part number is already listed for this manufacturer and vendor. A different vendor can use the same part number.',
                );
                return;
            }

            const input: Record<string, unknown> = {
                companyId,
                itemName: newItemName.trim(),
                category,
                manufacturer: manufacturer || undefined,
                partNumber: newPartNumber || undefined,
                vendorId: vendorId || undefined,
                vendorName: vendorName || undefined,
                price: newPrice ? parseFloat(newPrice) : undefined,
                unitOfMeasure: newUnit || undefined,
                itemType: 'Consumable',
                createdByUserId: currentUserId || undefined,
                createdByUserName: currentUserName || undefined,
            };

            if (costTrackingEnabled && expenseCategoryId.trim()) {
                const typed = expenseCategoryId.trim();
                const selected = expenseCategories.find(
                    (row) => row.id === typed || row.name.trim().toLowerCase() === typed.toLowerCase(),
                );
                if (selected) {
                    input.expenseCategoryId = selected.id;
                    input.expenseCategoryName = selected.name;
                } else {
                    const created = await createExpenseCategory({
                        companyId,
                        name: typed,
                        startingBalance: 0,
                    });
                    if (!created?.id) throw new Error('Failed to add cost category');
                    input.expenseCategoryId = created.id;
                    input.expenseCategoryName = created.name || typed;
                }
            }

            const item = await createServiceItem(input);
            if (!item) throw new Error('Failed to create service item');
            onAddItem(item as PMPartsAndProcedure);
            handleClose();
        } catch (err) {
            console.error('Failed to create service item:', err);
            setSaveError('Could not save this service item. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveNewProcedure = async () => {
        const name = newProcedureName.trim();
        const category = resolveCategory(newProcedureCategory, procedureCategories);
        if (!name || !category) return;
        setSaving(true);
        setSaveError('');
        try {
            const existing = procedures.find(
                (procedure) =>
                    procedure.category.trim().toLowerCase() === category.toLowerCase() &&
                    procedure.itemName.trim().toLowerCase() === name.toLowerCase(),
            );
            if (!existing) {
                const categorySortOrder = nextCategorySortOrder(procedures, category);
                await createServiceProcedure({
                    companyId,
                    category,
                    itemName: name,
                    ...(categorySortOrder != null ? { categorySortOrder } : {}),
                    createdByUserId: currentUserId || undefined,
                    createdByUserName: currentUserName || undefined,
                });
            }
            const matrixItem = await ensureProcedureOnMatrix(existing?.itemName || name, category);
            onAddItem(matrixItem);
            handleClose();
        } catch (err) {
            console.error('Failed to create procedure:', err);
            setSaveError('Could not save this procedure. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSelectProcedure = async (procedure: ServiceProcedure) => {
        setSaving(true);
        setSaveError('');
        try {
            const matrixItem = await ensureProcedureOnMatrix(procedure.itemName, procedure.category);
            onAddItem(matrixItem);
            handleClose();
        } catch (err) {
            console.error('Failed to add procedure:', err);
            setSaveError('Could not add this procedure. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const canSaveInline =
        activeTab === 'consumable'
            ? Boolean(newItemName.trim() && newCategory.trim())
            : Boolean(newProcedureName.trim() && newProcedureCategory.trim());

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={handleClose}
            header="Add to matrix"
            size="large"
            bodyClassName="pm-add-matrix-modal-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonOnClick={handleClose}
                        buttonDisabled={saving}
                    />
                    {showInlineForm ? (
                        <Btn
                            variant="primary"
                            buttonText={saving ? 'Saving...' : 'Save & add to matrix'}
                            buttonDisabled={saving || !canSaveInline}
                            buttonOnClick={() => {
                                void (activeTab === 'consumable' ? handleSaveNewConsumable() : handleSaveNewProcedure());
                            }}
                        />
                    ) : (
                        <Btn variant="primary" buttonText="Add to matrix" buttonDisabled />
                    )}
                </div>
            }
        >
            <TabNav
                variant="tabs"
                id="pm-add-matrix"
                ariaLabel="Add to matrix type"
                className="pm-add-matrix-tabs"
                tabs={[
                    { id: 'consumable', label: '$ Consumable' },
                    { id: 'procedure', label: 'Procedure' },
                ]}
                activeTabId={activeTab}
                onTabChange={(tabId) => {
                    setActiveTab(tabId as TabId);
                    setShowInlineForm(false);
                    setSearch('');
                    setSelectedCategory('');
                    setSaveError('');
                }}
            />

            {!showInlineForm ? (
                <div {...getTabPanelProps('pm-add-matrix', activeTab)}>
                    <label className="pm-modal-select-field">
                        <span className="pm-modal-select-label">Category</span>
                        <SearchSuggest
                            options={categoryOptions}
                            value={selectedCategory}
                            onChange={(category) => {
                                setSelectedCategory(category);
                                setSearch('');
                            }}
                            placeholder="Choose a category"
                            emptyMessage="No matching categories"
                            allowNew
                            newLabel="+ New category"
                            clearable
                            inputId="pm-add-matrix-category"
                            ariaLabel={activeTab === 'consumable' ? 'Consumable category' : 'Procedure category'}
                            onInputKeyDown={(event) => {
                                if (event.key !== 'Tab' || event.shiftKey) return;
                                const known = activeTab === 'consumable' ? consumableCategories : procedureCategories;
                                const typed = selectedCategory.trim();
                                if (!typed || findExistingCategoryName(known, typed)) return;
                                event.preventDefault();
                                document.getElementById('pm-matrix-add-new-item')?.focus();
                            }}
                        />
                    </label>

                    {selectedCategory ? (
                        <>
                            <InputField
                                type="search"
                                label={activeTab === 'consumable' ? 'Service items in this category' : 'Procedures in this category'}
                                value={search}
                                onChange={setSearch}
                                placeholder={activeTab === 'consumable' ? 'Search service items...' : 'Search procedures...'}
                                ariaLabel={activeTab === 'consumable' ? 'Search service items' : 'Search procedures'}
                            />

                            <div className="pm-matrix-modal-list" role="listbox">
                                {activeTab === 'consumable'
                                    ? filteredConsumables.map((item) => (
                                          <button
                                              type="button"
                                              key={item.id}
                                              className="pm-matrix-modal-list-item"
                                              onClick={() => handleSelectItem(item)}
                                          >
                                              <span className="pm-matrix-modal-list-name">{item.itemName}</span>
                                              <span className="pm-matrix-modal-list-meta">
                                                  {item.manufacturer && <span>{item.manufacturer}</span>}
                                                  {item.partNumber && <span>#{item.partNumber}</span>}
                                                  {item.vendorName && <span>{item.vendorName}</span>}
                                              </span>
                                          </button>
                                      ))
                                    : filteredProcedures.map((procedure) => (
                                          <button
                                              type="button"
                                              key={procedure.id}
                                              className="pm-matrix-modal-list-item"
                                              disabled={saving}
                                              onClick={() => {
                                                  void handleSelectProcedure(procedure);
                                              }}
                                          >
                                              <span className="pm-matrix-modal-list-name">{procedure.itemName}</span>
                                          </button>
                                      ))}
                                {(activeTab === 'consumable' ? filteredConsumables : filteredProcedures).length === 0 && (
                                    <div className="pm-matrix-modal-empty">No matching items in this category</div>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="pm-modal-hint">Choose a category to see its items.</p>
                    )}

                    {saveError && (
                        <p className="pm-modal-hint" role="alert">
                            {saveError}
                        </p>
                    )}

                    <Btn
                        variant="link"
                        buttonText={`+ Add new ${activeTab === 'consumable' ? 'service item' : 'procedure'}...`}
                        buttonAriaProps={{ id: 'pm-matrix-add-new-item' }}
                        buttonOnClick={() => {
                            const known = activeTab === 'consumable' ? consumableCategories : procedureCategories;
                            const typed = resolveCategory(selectedCategory, known);
                            if (activeTab === 'consumable') setNewCategory(typed);
                            else setNewProcedureCategory(typed);
                            setShowInlineForm(true);
                        }}
                        buttonClasses="pm-matrix-modal-add-new-link"
                    />
                </div>
            ) : activeTab === 'consumable' ? (
                <div {...getTabPanelProps('pm-add-matrix', 'consumable')} className="pm-modal-fields">
                    <p className="pm-matrix-modal-inline-header">+ New service item</p>

                    <label className="pm-modal-select-field">
                        <span className="pm-modal-select-label">Category</span>
                        <SearchSuggest
                            options={consumableCategories.map((name) => ({ value: name, label: name }))}
                            value={newCategory}
                            onChange={setNewCategory}
                            placeholder="Type to search or add"
                            newLabel="+ New Category"
                            allowNew
                            inputId="pm-add-matrix-new-item-category"
                            ariaLabel="Category"
                        />
                    </label>

                    <InputField
                        label="Description"
                        value={newItemName}
                        onChange={setNewItemName}
                        placeholder="e.g. Transmission Filter"
                        ariaLabel="Description"
                    />

                    <label className="pm-modal-select-field">
                        <span className="pm-modal-select-label">Manufacturer</span>
                        <SearchSuggest
                            options={manufacturersForCategory.map((name) => ({ value: name, label: name }))}
                            value={newManufacturer}
                            onChange={setNewManufacturer}
                            placeholder="Choose a manufacturer or add one"
                            allowNew
                            newLabel="+ New manufacturer"
                            emptyMessage="No manufacturers for this category yet"
                            inputId="pm-add-matrix-manufacturer"
                            ariaLabel="Manufacturer"
                        />
                    </label>

                    <div className="pm-modal-row">
                        <InputField
                            label="Part #"
                            value={newPartNumber}
                            onChange={setNewPartNumber}
                            placeholder="TF-300"
                            ariaLabel="Part number"
                        />
                        <div className="pm-modal-vendor-field">
                            <span className="pm-modal-select-label">Vendor</span>
                            {!showNewVendorInput ? (
                                <SearchSuggest
                                    options={vendors.map((v) => ({ value: v.id, label: v.name || '' }))}
                                    value={newVendorId}
                                    onChange={(vendorId) => {
                                        setNewVendorId(vendorId);
                                        const vendor = vendors.find((v) => v.id === vendorId);
                                        setNewVendor(vendor?.name || '');
                                    }}
                                    placeholder="Search vendors..."
                                    allowNew={false}
                                    actionOption={{
                                        label: '+ Create new vendor',
                                        onClick: ({ searchText }) => {
                                            setNewVendorId('');
                                            setNewVendor('');
                                            setNewVendorName(searchText);
                                            setShowNewVendorInput(true);
                                        },
                                    }}
                                    inputId="pm-add-matrix-vendor"
                                    ariaLabel="Vendor"
                                />
                            ) : (
                                <div className="pm-matrix-new-vendor">
                                    <div className="pm-matrix-new-vendor-header">
                                        <span className="pm-matrix-new-vendor-title">+ New vendor</span>
                                        <Btn
                                            variant="link"
                                            buttonText="Cancel"
                                            buttonOnClick={() => {
                                                setShowNewVendorInput(false);
                                                setNewVendorName('');
                                                setNewVendorAddress('');
                                                setNewVendorPhone('');
                                                setNewVendorFax('');
                                                setNewVendorRepName('');
                                                setNewVendorRepPhone('');
                                            }}
                                        />
                                    </div>
                                    <InputField
                                        label="Vendor name"
                                        value={newVendorName}
                                        onChange={setNewVendorName}
                                        placeholder="e.g. CAT Dealer"
                                        autoFocus
                                        ariaLabel="Vendor name"
                                    />
                                    <InputField
                                        label="Address"
                                        value={newVendorAddress}
                                        onChange={setNewVendorAddress}
                                        placeholder="123 Main St"
                                        ariaLabel="Vendor address"
                                    />
                                    <div className="pm-modal-row">
                                        <InputField
                                            label="Phone"
                                            type="tel"
                                            value={newVendorPhone}
                                            onChange={setNewVendorPhone}
                                            placeholder="(555) 123-4567"
                                            ariaLabel="Vendor phone"
                                        />
                                        <InputField
                                            label="Fax"
                                            type="tel"
                                            value={newVendorFax}
                                            onChange={setNewVendorFax}
                                            placeholder="(555) 123-4568"
                                            ariaLabel="Vendor fax"
                                        />
                                    </div>
                                    <div className="pm-modal-row">
                                        <InputField
                                            label="Rep name"
                                            value={newVendorRepName}
                                            onChange={setNewVendorRepName}
                                            placeholder="Contact name"
                                            ariaLabel="Vendor representative name"
                                        />
                                        <InputField
                                            label="Rep phone"
                                            type="tel"
                                            value={newVendorRepPhone}
                                            onChange={setNewVendorRepPhone}
                                            placeholder="(555) 987-6543"
                                            ariaLabel="Vendor representative phone"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pm-modal-row">
                        <InputField
                            label="Price"
                            type="number"
                            min={0}
                            step="0.01"
                            value={newPrice}
                            onChange={setNewPrice}
                            placeholder="$"
                            ariaLabel="Price"
                        />
                        <InputField
                            label="Unit"
                            value={newUnit}
                            onChange={setNewUnit}
                            placeholder="Each"
                            ariaLabel="Unit of measure"
                        />
                    </div>

                    {costTrackingEnabled && (
                        <label className="pm-modal-select-field">
                            <span className="pm-modal-select-label">Cost category</span>
                            <SearchSuggest
                                options={[...expenseCategories]
                                    .sort((a, b) =>
                                        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
                                    )
                                    .map((row) => ({ value: row.id, label: row.name }))}
                                value={expenseCategoryId}
                                onChange={setExpenseCategoryId}
                                placeholder="Type to search or add"
                                allowNew
                                newLabel="+ Add cost category"
                                resolveValue={(text) =>
                                    expenseCategories.find(
                                        (row) => row.name.trim().toLowerCase() === text.trim().toLowerCase(),
                                    )?.id || text.trim()
                                }
                                formatDisplayValue={(value) =>
                                    expenseCategories.find((row) => row.id === value)?.name || value
                                }
                                inputId="pm-add-matrix-cost-category"
                                ariaLabel="Cost category"
                            />
                        </label>
                    )}

                    <p className="pm-modal-hint">
                        <strong>Saves to Resources → Service Items</strong> and gets added to the matrix in one step.
                    </p>
                    {saveError && (
                        <p className="pm-modal-hint" role="alert">
                            {saveError}
                        </p>
                    )}
                </div>
            ) : (
                <div {...getTabPanelProps('pm-add-matrix', 'procedure')} className="pm-modal-fields">
                    <p className="pm-matrix-modal-inline-header">+ New procedure</p>

                    <label className="pm-modal-select-field">
                        <span className="pm-modal-select-label">Category</span>
                        <SearchSuggest
                            options={procedureCategories.map((name) => ({ value: name, label: name }))}
                            value={newProcedureCategory}
                            onChange={setNewProcedureCategory}
                            placeholder="Type to search or add"
                            newLabel="+ New Category"
                            allowNew
                            inputId="pm-add-matrix-new-procedure-category"
                            ariaLabel="Procedure category"
                        />
                    </label>

                    <InputField
                        label="Procedure name"
                        value={newProcedureName}
                        onChange={setNewProcedureName}
                        placeholder="e.g. Grease Boom Pivot Pins"
                        ariaLabel="Procedure name"
                    />

                    <p className="pm-modal-hint">
                        <strong>Saves to Resources → Procedures</strong> and adds the row in one step. No part #,
                        vendor, or price — a procedure carries no cost.
                    </p>
                    {saveError && (
                        <p className="pm-modal-hint" role="alert">
                            {saveError}
                        </p>
                    )}
                </div>
            )}
        </CustomModal>
    );
};
